import { post } from './API';
// `isNotDeployed` and the field-message reader describe how the
// `/ai/instructions/*` Lambda answers a route it has no handler for, which
// is that service's territory and is shared by both unbuilt seams.
import { apiFieldMessages, isNotDeployed } from './AiInstructionsService';

/**
 * THE DRY RUN — one saved draft, one real past email, the whole trace.
 *
 * Nothing is sent, queued, published or recorded. The verb is POST only
 * because a run is work, not a lookup — nothing is created, nothing is
 * queued, and nothing leaves the building.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE ENDPOINT DOES NOT EXIST YET. THIS FILE IS THE CONTRACT, NOT A MIRROR.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * It is being built alongside the drafting agent. Everything below is what
 * the dashboard ASKS FOR, written down so the two halves can be built at
 * once and so the assumption is reviewable rather than buried in a component:
 *
 *   POST /ai/instructions/dry-run  { scope, version, email_key }
 *
 * `version`, NOT A BODY — and that is the load-bearing choice. `publish`
 * already takes a stored version rather than the contents of the editor, for
 * the reason the screen is built around: an edit half-typed must never be
 * able to look live. A dry run that took the textarea's current value would
 * let an admin test text that was never stored and then publish a version
 * that was never tested. Taking the version makes "what you tested is what
 * you publish" structural instead of a thing to remember, and it reuses the
 * save-before-you-act gate the screen already has.
 *
 * `email_key` is the S3 object key the inbox browser already lists and reads
 * (`GET /email-inbox?key=…`). The dashboard sends the KEY, not the email: the
 * server can fetch it itself, the body of a forwarded thread runs to
 * megabytes, and a run identified by a key can be repeated later against the
 * same input while one identified by pasted HTML cannot.
 *
 * ── WHAT COMES BACK ──
 *
 *   {
 *     scope, version,
 *     email:       { key, subject, from, date },
 *     trace:       [ { tool, authority, arguments, outcome,
 *                      result, error, duration_ms } ],
 *     output:      <whatever the agent produced; the scope's own shape>,
 *     model, duration_ms, tokens: { input, output }, cost_usd,
 *     executed:    false,
 *   }
 *
 * THE TRACE IS THE PRODUCT. A run that shows WHICH TOOL THE AGENT CHOSE, in
 * order, with what it passed and what came back, tells an admin whether the
 * rule they just wrote works. A single verdict does not, and neither does a
 * form that invokes one tool with hand-typed parameters — that tests the
 * tool, and the tool is not what is being edited here.
 *
 * `executed: false` IS THE SERVER SAYING SO. The screen states the guarantee
 * either way, but a run that came back claiming it HAD executed something
 * would be a contract breach worth surfacing rather than hiding, so it is
 * read rather than assumed.
 *
 * The response is deliberately shaped like one `ai_decisions` row plus the
 * per-tool trace that row does not keep — `model`, `duration_ms`, `tokens`
 * and `output` are the names the Activity screen already reads. A dry run is
 * a decision that was not recorded, and when wave 2 starts recording the
 * intercepted calls it should not have to invent a second vocabulary.
 */

export { isNotDeployed };

/** The path the contract above names. Here so a test can assert it once. */
export const DRY_RUN_PATH = '/ai/instructions/dry-run';

/**
 * WHAT HAPPENED TO ONE TOOL CALL THE AGENT CHOSE TO MAKE.
 *
 * Three of the four words are `TOOL_OUTCOMES` from `AiActivityService`, which
 * is the vocabulary the decision log is already written in, and the tones
 * follow the house rule that AMBER MEANS A HUMAN IS NEEDED and never that
 * something broke.
 *
 * `held` IS THE FOURTH, and it is why this is not simply an import of that
 * list. `TOOL_OUTCOMES` mirrors a server enum for recorded decisions and has
 * no member for "the agent called a write, and the call was captured instead
 * of executed" — which in a dry run is the single most important thing to be
 * able to say, and the normal outcome for every write. Adding it to the
 * Activity mirror would put a word in it that the server's enum does not
 * have; so it lives here, with the overlap stated.
 *
 * A dry run IS shadow mode: reads execute, writes are captured. Anything
 * else would make the trace worthless — an agent that cannot look up the
 * thread, the booking or the price does not propose a reply, it invents one.
 */
export const TRACE_OUTCOMES = [
  { value: 'ok', label: 'Ran', tone: 'good' },
  { value: 'held', label: 'Held — not executed', tone: 'warn' },
  { value: 'denied', label: 'Denied', tone: 'bad' },
  { value: 'degraded', label: 'Degraded', tone: 'bad' },
  { value: 'error', label: 'Error', tone: 'bad' },
];

/**
 * A descriptor by value, so an outcome the server adds later still renders.
 *
 * The same fallback as `AiActivityService.stateDescriptor`, for the same
 * reason and one sharper: a step that fell through to nothing would leave the
 * trace LOOKING CLEAN about a call it could not account for, and a trace that
 * is read to decide whether a rule works must not be able to do that.
 */
export const traceOutcomeDescriptor = (value) => (
  TRACE_OUTCOMES.find((entry) => entry.value === value)
  || (value ? { value, label: String(value), tone: 'muted' } : { value: '', label: 'No outcome given', tone: 'muted' })
);

/** Did this step actually run? Anything that is not `ok` did not. */
export const didRun = (step) => step?.outcome === 'ok';

/**
 * Why a run was refused, as the admin should read it.
 *
 * The field messages first, for the same reason the builder's save reads them:
 * a ValidationError from this API puts a constant string in `message` and the
 * actual reason in `errors[]`.
 */
export const dryRunErrorText = (err, fallback = 'The dry run failed.') => (
  apiFieldMessages(err).join('; ')
  || err?.response?.data?.message
  || err?.message
  || fallback
);

export default {
  /**
   * Run one SAVED draft against one past email.
   *
   * Returned already unpacked, with every key defaulted — the shape
   * `AiActivityService.list` uses, so a reset jest mock answering `undefined`
   * renders an empty trace rather than throwing.
   *
   * `executed` defaults to TRUE when the server did not say, which is the
   * only safe direction: the screen's promise that nothing ran must come from
   * the server having said so, not from a key being missing.
   */
  run: async ({ scope, version, emailKey }) => {
    const response = await post(DRY_RUN_PATH, {
      scope,
      version,
      email_key: emailKey,
    });
    const body = response?.data?.data || response?.data || {};
    return {
      scope: body.scope || scope,
      version: body.version === undefined ? version : body.version,
      email: body.email || null,
      trace: body.trace || [],
      output: body.output,
      model: body.model || null,
      duration_ms: body.duration_ms,
      tokens: body.tokens || null,
      cost_usd: body.cost_usd,
      executed: body.executed !== false,
    };
  },
};
